import { ServerSentEvents } from "@portal/api/server-sent-events/ServerSentEvents";
import { ErrorFallbackUI } from "@portal/components/ui/ErrorFallbackUI";
import { AuthenticationErrorBoundary } from "@portal/components/utils/AuthenticationErrorBoundary";
import { AmplitudeProvider } from "@portal/providers/AmplitudeProvider";
import { AuthenticatedUserProvider } from "@portal/providers/AuthenticatedUserProvider";
import { AuthProvider } from "@portal/providers/AuthProvider";
import { GifProvider } from "@portal/providers/GifProvider";
import { GlobalErrorHandlingProvider } from "@portal/providers/GlobalErrorHandlingProvider";
import { GlobalFlagsProvider } from "@portal/providers/GlobalFlagsProvider";
import { GlobalSearchProvider } from "@portal/providers/GlobalSearchProvider";
import { PendingBondRequestOutcomeProvider } from "@portal/providers/PendingBondRequestOutcomeProvider";
import { PendoProvider } from "@portal/providers/PendoProvider";
import { QueryClientProvider } from "@portal/providers/QueryClientProvider";
import * as Sentry from "@sentry/react";
import { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ErrorBoundary } from "react-error-boundary";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { AppRouter } from "./AppRouter";
import { getConfig } from "./config";
import { isApiError, isAuthenticationError } from "./utils/error";

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 60000 * 5,
			gcTime: 60000 * 5,
			refetchOnWindowFocus: false,
			refetchIntervalInBackground: false,
			retry: (failureCount, error) => {
				if (isApiError(error) && !RETRYABLE_STATUS_CODES.has(error.statusCode)) {
					return false;
				}
				return failureCount < 3;
			},
			throwOnError: isAuthenticationError,
		},
		mutations: {
			retry: (failureCount, error) => {
				if (error instanceof Error && error.message.includes("Failed to fetch")) {
					return failureCount < 3;
				}
				return false;
			},
		},
	},
});

function AppLayout() {
	const config = getConfig();

	return (
		<ErrorBoundary FallbackComponent={ErrorFallbackUI}>
			<GlobalErrorHandlingProvider>
				<AuthProvider>
					<AuthenticatedUserProvider>
						<AuthenticationErrorBoundary>
							<PendoProvider>
								<AmplitudeProvider />
								<QueryClientProvider queryClient={queryClient}>
									<GlobalFlagsProvider>
										<ServerSentEvents />
										<PendingBondRequestOutcomeProvider>
											<GifProvider>
												<GlobalSearchProvider>
													<Outlet />
												</GlobalSearchProvider>
											</GifProvider>
										</PendingBondRequestOutcomeProvider>
									</GlobalFlagsProvider>
									{config.environment === "local" && config.tanstackQueryDevtools && (
										<ReactQueryDevtools initialIsOpen={false} />
									)}
								</QueryClientProvider>
							</PendoProvider>
						</AuthenticationErrorBoundary>
					</AuthenticatedUserProvider>
				</AuthProvider>
			</GlobalErrorHandlingProvider>
		</ErrorBoundary>
	);
}

const router = createBrowserRouter([
	{
		element: <AppLayout />,
		children: [{ path: "*", element: <AppRouter /> }],
	},
]);

function App() {
	return <RouterProvider router={router} />;
}

export default Sentry.withProfiler(App);
